import { CountriesAlpha2 } from '@sequencehq/api'
import { statesForCountry } from '@sequencehq/api/utils/commonEnums'
import {
  Address,
  CustomerAddress,
  getBillingAddress,
  getShippingAddress,
  isShippingSameAsBilling,
  toCustomerAddresses
} from '@sequencehq/api/utils/commonModels'
import { CustomerFormAddress, CustomerFormFieldsData } from './types'

export const EMPTY_CUSTOMER_FORM_ADDRESS: CustomerFormAddress = {
  country: '',
  line1: '',
  line2: undefined,
  town: '',
  state: undefined,
  postcode: ''
}

export const toCustomerFormAddress = (
  address: Address | null,
  defaultCountry?: CountriesAlpha2
): CustomerFormAddress => ({
  country: address?.country ?? defaultCountry ?? '',
  line1: address?.line1 ?? '',
  line2: address?.line2,
  town: address?.town ?? '',
  state: address?.state,
  postcode: address?.postcode ?? ''
})

/**
 * A blank country means the address was cleared or never set, so we return
 * no address at all rather than one made of empty strings.
 */
export const fromCustomerFormAddress = (
  formAddress: CustomerFormAddress
): Address | undefined => {
  if (!formAddress.country) {
    return undefined
  }

  const countryStates = statesForCountry(formAddress.country)
  const hasValidState = formAddress.state
    ? countryStates.includes(formAddress.state)
    : false

  return {
    country: formAddress.country,
    line1: formAddress.line1.trim(),
    line2: formAddress.line2?.trim() || undefined,
    town: formAddress.town.trim(),
    state: hasValidState ? formAddress.state : undefined,
    postcode: formAddress.postcode.trim()
  }
}

/**
 * The shipping fields start blank when shipping is the same as billing, so
 * unticking "Same as billing address" gives the user an empty address to fill
 * in.
 */
export const toCustomerFormAddresses = (
  addresses: CustomerAddress[] | undefined,
  options?: { defaultBillingCountry?: CountriesAlpha2 }
): Pick<
  CustomerFormFieldsData,
  'address' | 'shippingAddress' | 'isShippingSameAsBilling'
> => {
  const isSameAsBilling = isShippingSameAsBilling(addresses)

  return {
    address: toCustomerFormAddress(
      getBillingAddress(addresses),
      options?.defaultBillingCountry
    ),
    shippingAddress: isSameAsBilling
      ? EMPTY_CUSTOMER_FORM_ADDRESS
      : toCustomerFormAddress(getShippingAddress(addresses)),
    isShippingSameAsBilling: isSameAsBilling
  }
}

export const toCustomerAddressesFromForm = (
  data: Pick<
    CustomerFormFieldsData,
    'address' | 'shippingAddress' | 'isShippingSameAsBilling'
  >
): CustomerAddress[] =>
  toCustomerAddresses({
    billingAddress: fromCustomerFormAddress(data.address),
    shippingAddress: fromCustomerFormAddress(data.shippingAddress),
    isShippingSameAsBilling: data.isShippingSameAsBilling
  })
